class ProductMinQuantityGuard extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const formId = this.dataset.formId;
    const min = parseInt(this.dataset.minQuantity || '1', 10);
    const message = this.dataset.message || `Minimum quantity is ${min}.`;
    const requireArtwork = this.dataset.requireArtwork === 'true';
    const artworkProperty = this.dataset.artworkProperty || 'Artwork';
    const artworkMessage = this.dataset.artworkMessage || 'Please upload artwork.';

    if (!formId) return;

    // The quantity input may be inserted by another component after our own
    // connectedCallback runs. Apply the MOQ once now and again on the next frame
    // to cover both cases.
    this.#enforceMin(formId, min);
    requestAnimationFrame(() => this.#enforceMin(formId, min));

    // Find the quantity selector outside the form so cart-page-style outside
    // bindings still work; the product form lives inside the same section.
    const findForm = () => document.getElementById(formId);

    const submitHandler = (event) => {
      const form = event.currentTarget;
      if (!(form instanceof HTMLFormElement)) return;
      const qtyInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="quantity"]'));
      const qty = parseInt(qtyInput?.value || '1', 10);
      if (qty < min) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#announce(form, message);
        return;
      }
      if (requireArtwork) {
        const fileInput = /** @type {HTMLInputElement | null} */ (
          document.querySelector(`input[type="file"][name="properties[${artworkProperty}]"][form="${formId}"]`)
          ?? form.querySelector(`input[type="file"][name="properties[${artworkProperty}]"]`)
        );
        if (fileInput && (!fileInput.files || fileInput.files.length === 0)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#announce(form, artworkMessage);
          fileInput.focus();
        }
      }
    };

    const form = findForm();
    if (form instanceof HTMLFormElement) {
      form.addEventListener('submit', submitHandler, { signal, capture: true });
    }

    // Quote workflow: when a redirect URL is configured, send the customer
    // to that page after a successful add-to-cart. Aligns this template
    // with the request-quote-form section, which reads cart contents.
    const redirectUrl = this.dataset.quoteRedirectUrl;
    if (redirectUrl) {
      document.addEventListener(
        'cart:update',
        (event) => {
          const detail = /** @type {any} */ (event).detail;
          if (!detail || !detail.data) return;
          if (detail.data.source !== 'product-form-component') return;
          if (detail.data.didError) return;
          window.location.assign(redirectUrl);
        },
        { signal }
      );
    }
  }

  /**
   * Sets the min and clamps the value of the quantity input belonging to the
   * given product form. Also refreshes the quantity-selector button states.
   * @param {string} formId
   * @param {number} min
   */
  #enforceMin(formId, min) {
    if (!Number.isFinite(min) || min <= 1) return;
    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return;
    const qtyInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="quantity"]'));
    if (!qtyInput) return;

    qtyInput.min = String(min);
    qtyInput.setAttribute('data-min', String(min));
    const current = parseInt(qtyInput.value, 10);
    if (!Number.isFinite(current) || current < min) {
      qtyInput.value = String(min);
    }

    const selector = qtyInput.closest('quantity-selector-component');
    if (selector && typeof (/** @type {any} */ (selector)).updateButtonStates === 'function') {
      /** @type {any} */ (selector).updateButtonStates();
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #announce(form, message) {
    const errorEl = form.querySelector('.product-form-text__error');
    if (errorEl instanceof HTMLElement) {
      errorEl.classList.remove('hidden');
      let textNode = errorEl.querySelector('.product-form-text__error-message');
      if (!textNode) {
        textNode = document.createElement('span');
        textNode.className = 'product-form-text__error-message';
        errorEl.appendChild(textNode);
      }
      textNode.textContent = ` ${message}`;
      setTimeout(() => errorEl.classList.add('hidden'), 6000);
    } else {
      alert(message);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}

if (!customElements.get('product-min-quantity-guard')) {
  customElements.define('product-min-quantity-guard', ProductMinQuantityGuard);
}
